import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { Toast } from '../../../components';
import { TalentActivityData } from '../types';

interface ActivitiesSectionProps {
  activities: TalentActivityData[];
  onViewActivity?: (activityId: number) => void;
  onAddActivity?: () => void;
  readOnly?: boolean;
}

function ActivityCard({ activity, onView }: { activity: TalentActivityData; onView?: (id: number) => void }) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <View
      style={[
        cardStyles.container,
        {
          backgroundColor: colors.card,
          borderColor: colors.cardBorder,
        },
      ]}
    >
      <Text style={[cardStyles.title, { color: colors.textPrimary }]}>
        {activity.name}
      </Text>

      {/* Progress bar row — RTL: bar fills from right, dot on left edge of fill */}
      <View style={cardStyles.progressRow}>
        <View style={cardStyles.pctBadge}>
          <Text style={[cardStyles.pctText, { color: colors.textPrimary }]}>
            % {activity.progress}
          </Text>
        </View>
        <View style={[cardStyles.trackBg, { backgroundColor: '#F1F5F9' }]}>
          {/* Fill from right side */}
          <View
            style={[
              cardStyles.trackFill,
              { width: `${activity.progress}%`, backgroundColor: '#2ca02c' },
            ]}
          />
          {/* Green dot at left edge of fill */}
          <View style={cardStyles.dotGreen} />
        </View>
      </View>

      {/* View Details */}
      <TouchableOpacity style={cardStyles.linkRow} onPress={() => onView?.(activity.id)}>
        <Ionicons name="chevron-back" size={14} color={colors.textPrimary} />
        <Text style={[cardStyles.linkText, { color: colors.textPrimary }]}>
          {isRTL ? 'عرض التفاصيل' : 'View Details'}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

export default function ActivitiesSection({ activities, onViewActivity, onAddActivity, readOnly }: ActivitiesSectionProps) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [toastVisible, setToastVisible] = useState(false);

  const handleAdd = () => {
    if (onAddActivity) { onAddActivity(); return; }
    setToastVisible(true);
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.card,
          borderColor: colors.cardBorder,
        },
        createShadow(0, 1, 3, '#000', 0.1),
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        {!readOnly && (
          <TouchableOpacity style={[styles.addBtn, { borderColor: '#CBD5E1' }, createShadow(0, 1, 3, '#000', 0.1)]} onPress={handleAdd}>
            <Text style={[styles.addBtnText, { color: colors.textPrimary }]}>
              {isRTL ? 'إضافة نشاط' : 'Add Activity'}
            </Text>
            <Ionicons name="add" size={14} color={colors.textPrimary} />
          </TouchableOpacity>
        )}
        <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
          {isRTL ? 'الأنشطة' : 'Activities'}
        </Text>
      </View>

      {/* Cards — right-aligned with empty space on left (Figma: 4 flex columns, 2 empty + 2 cards) */}
      <View style={[styles.cardsRow, { backgroundColor: colors.backgroundAlt || '#F1F5F9' }]}>
        <View style={styles.cardsSpacerLeft} />
        <View style={styles.cardsSpacerLeft} />
        {activities.map((activity) => (
          <ActivityCard key={activity.id} activity={activity} onView={onViewActivity} />
        ))}
      </View>
      <Toast visible={toastVisible} message={isRTL ? 'قريباً' : 'Coming soon'} onHide={() => setToastVisible(false)} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    flex: 1,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  addBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  cardsRow: {
    flexDirection: 'row',
    gap: 20,
    borderRadius: 8,
    padding: 12,
  },
  cardsSpacerLeft: {
    flex: 1,
  },
});

const cardStyles = StyleSheet.create({
  container: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pctBadge: {
    backgroundColor: '#F1F5F9',
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pctText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  } as any,
  dotGreen: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#2ca02c',
    marginRight: -5,
    zIndex: 2,
  },
  trackBg: {
    flex: 1,
    height: 8,
    borderRadius: 8,
    flexDirection: 'row-reverse' as const,
    alignItems: 'center' as const,
  },
  trackFill: {
    height: '100%',
    borderRadius: 8,
    zIndex: 1,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  linkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
});
